/**
 * Shared rendering for a partner office and its access state.
 *
 * One implementation, used by Network > People and by a single person's page.
 * These were duplicated per page and immediately drifted - the access column on
 * one said something the other did not - which is exactly what the shared
 * component rule exists to prevent.
 *
 * Tokens and label logic live in ``partner-office-access.ts`` so this file only
 * exports components (react-refresh), mirroring the currency-options split.
 */

import type { MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ACCESS_TONE } from '@/admin/components/shared/partner-office-access'
import type { OfficeAccess, PartnerOffice } from '@/admin/hooks/use-network-people'
import { cn } from '@/shared/lib/utils'

export function AccessBadge({
  state,
  label,
}: {
  state: OfficeAccess | 'mixed'
  label: string
}) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]',
        state === 'mixed'
          ? 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
          : ACCESS_TONE[state],
      )}
    >
      {label}
    </span>
  )
}

/** ``Head office`` / ``Location`` - plain words, not Parent / Subsidiary. */
export function OfficeTierBadge({ office }: { office: PartnerOffice }) {
  const { t } = useTranslation()
  if (office.is_head_office) {
    return (
      <span className="inline-flex h-4 shrink-0 items-center rounded-full border border-[var(--green-border)] px-1.5 text-[9px] uppercase tracking-[0.06em] text-[var(--green)]">
        {t('network.people.headOffice', 'Head office')}
      </span>
    )
  }
  if (office.parent_name) {
    return (
      <span className="inline-flex h-4 shrink-0 items-center rounded-full border border-[var(--rule)] px-1.5 text-[9px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
        {t('network.people.location', 'Location')}
      </span>
    )
  }
  return null
}

/**
 * ``Head office > Location``, with each half opening that partner's record.
 *
 * Both halves are separately clickable: an admin reading a person often wants
 * the group, not the branch. The parent is only a link when the head office is
 * itself a partner of this organisation.
 */
export function OfficeName({
  office,
  onOpen,
}: {
  office: PartnerOffice
  onOpen?: (partnershipUuid: string) => void
}) {
  const linkClass =
    'cursor-pointer underline-offset-2 transition-colors duration-100 hover:text-[var(--green)] hover:underline'

  const open = (uuid: string | null) => (event: ReactMouseEvent) => {
    if (!uuid || !onOpen) return
    // The whole table row is a link too; this must not also fire that.
    event.stopPropagation()
    event.preventDefault()
    onOpen(uuid)
  }

  const parentIsLink = Boolean(office.parent_partnership_uuid && onOpen)

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {office.parent_name ? (
        <>
          <span
            role={parentIsLink ? 'link' : undefined}
            tabIndex={parentIsLink ? 0 : undefined}
            onClick={open(office.parent_partnership_uuid)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !parentIsLink) return
              event.stopPropagation()
              onOpen?.(office.parent_partnership_uuid as string)
            }}
            className={cn('truncate text-[var(--ink-3)]', parentIsLink && linkClass)}
          >
            {office.parent_name}
          </span>
          <span className="shrink-0 text-[11px] text-[var(--ink-3)]">&rsaquo;</span>
        </>
      ) : null}
      <span
        role={onOpen ? 'link' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onClick={open(office.partnership_uuid)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !onOpen) return
          event.stopPropagation()
          onOpen(office.partnership_uuid)
        }}
        className={cn('truncate text-[var(--ink)]', onOpen && linkClass)}
      >
        {office.name}
      </span>
    </span>
  )
}
